import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Contact, Building2, Search, Inbox, RefreshCw, ArrowRightCircle, X,
  LayoutGrid, List as ListIcon, Plus, Upload, AlertTriangle, MessageSquare,
} from 'lucide-react';
import { DragDropContext, Droppable, Draggable, type DropResult } from '@hello-pangea/dnd';
import toast from 'react-hot-toast';
import { crmApi, workflowApi, workItemApi, type CrmClient, type Lead } from '../services/api';
import { useAppStore } from '../store/useAppStore';
import { useCrmSellerStore } from '../store/useCrmSellerStore';
import SellingAs from '../components/crm/SellingAs';

/**
 * Whether Convert is offered. Keyed on the LINK, not the stage: a lead moved
 * into Converted by hand before that was refused has no opportunity, and
 * hiding the button there is what left LD-17 with no way forward.
 */
function canConvert(lead: Lead): boolean {
  return !lead.convertedToId && lead.status !== 'disqualified';
}

interface PipelineState {
  id: string;
  label: string;
  color?: string;
  order?: number;
  isTerminal?: boolean;
  wipLimit?: number;
}

/**
 * Inbound leads, as a pipeline board or a list.
 *
 * Stages come from the workflow bound to the `lead` type, NOT the global status
 * list — that is the whole point of binding a workflow per work item type.
 * Hard-coding new/contacted/qualified here would mean editing the pipeline in
 * the workflow author silently stopped matching this page.
 *
 * Dragging a card asks the ENGINE to move it. An illegal move is rejected by
 * the server and the card snaps back, because the board must not be able to put
 * a lead somewhere the API would refuse.
 */
export default function LeadsPage() {
  const { setDetailItemId } = useAppStore();
  const navigate = useNavigate();
  const [view, setView] = useState<'board' | 'list'>('board');
  const [converting, setConverting] = useState<Lead | null>(null);
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [stages, setStages] = useState<PipelineState[]>([]);
  const [sources, setSources] = useState<string[]>([]);
  const [projectIds, setProjectIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [source, setSource] = useState('');
  const [moving, setMoving] = useState<string | null>(null);
  // `?convert=<lead id>` — the lead slideout's Convert button lands here.
  const [params, setParams] = useSearchParams();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await crmApi.listLeads();
      setLeads(data.leads || []);
      setSources(data.sources || []);
      setProjectIds(data.projectIds || []);

      const projectId = (data.projectIds || [])[0];
      try {
        const wf = await workflowApi.resolve({ projectId, workItemType: 'lead' });
        setStages(((wf?.states || []) as PipelineState[]).slice()
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
      } catch {
        setStages([]);
      }
    } catch (err: any) {
      toast.error(err.message || 'Could not load leads');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const id = params.get('convert');
    if (!id || loading) return;
    const lead = leads.find((l) => l.id === id);
    if (lead && canConvert(lead)) setConverting(lead);
    else if (lead) toast(`${lead.displayId} is already converted`);
    // Consumed once, so a refresh does not reopen the dialog.
    params.delete('convert');
    setParams(params, { replace: true });
  }, [params, setParams, loading, leads]);


  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return leads.filter((l) => {
      if (source && l.source !== source) return false;
      if (!q) return true;
      return [l.name, l.email, l.organisation, l.title, l.message]
        .some((v) => (v || '').toLowerCase().includes(q));
    });
  }, [leads, search, source]);

  const byStage = useMemo(() => {
    const buckets = new Map<string, Lead[]>();
    stages.forEach((s) => buckets.set(s.id, []));
    filtered.forEach((l) => {
      const key = l.status || 'new';
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key)!.push(l);
    });
    // Oldest first within a stage: the lead waiting longest needs attention
    // most, which is the opposite of the arrival sort used for the list.
    buckets.forEach((rows) =>
      rows.sort((a, b) => (a.createdAt || '').localeCompare(b.createdAt || '')));
    return buckets;
  }, [filtered, stages]);

  const stageFor = (id: string) => stages.find((s) => s.id === id);

  const onDragEnd = async (result: DropResult) => {
    const { draggableId, source: from, destination } = result;
    if (!destination || destination.droppableId === from.droppableId) return;

    const target = destination.droppableId;
    const lead = leads.find((l) => l.id === draggableId);
    if (!lead) return;

    // Converted means an opportunity exists, and only conversion creates one —
    // the server refuses the bare status move. Dropping on the column is
    // taken as the intent to convert, so it opens the conversion instead.
    if (target === 'converted' && !lead.convertedToId) {
      setConverting(lead);
      return;
    }

    // Optimistic, then reconciled. The engine is the authority: if it refuses
    // the move the card goes back where it was.
    const previous = lead.status;
    setLeads((ls) => ls.map((l) => (l.id === draggableId ? { ...l, status: target } : l)));
    setMoving(draggableId);
    try {
      await workItemApi.updateStatus(draggableId, target);
    } catch (err: any) {
      setLeads((ls) => ls.map((l) => (l.id === draggableId ? { ...l, status: previous } : l)));
      toast.error(err.message || `That move is not allowed from ${previous}`);
    } finally {
      setMoving(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-accent border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-400">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Contact size={20} className="text-accent" />
            Leads
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Enquiries from the website, conversations, events and imports.{' '}
            <span className="text-gray-400 dark:text-gray-500">
              {stages.length
                ? 'Stages come from the sales pipeline.'
                : 'No pipeline workflow bound — grouped by raw status.'}
            </span>
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <SellingAs />
          <div className="flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden">
            <button
              onClick={() => setView('board')}
              title="Board"
              className={`px-2 py-1.5 ${view === 'board' ? 'bg-accent/10 text-accent' : 'text-gray-500'}`}
            >
              <LayoutGrid size={14} />
            </button>
            <button
              onClick={() => setView('list')}
              title="List"
              className={`px-2 py-1.5 ${view === 'list' ? 'bg-accent/10 text-accent' : 'text-gray-500'}`}
            >
              <ListIcon size={14} />
            </button>
          </div>
          <button
            onClick={() => setCreating(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-accent text-white"
          >
            <Plus size={14} /> New lead
          </button>
          <button
            onClick={() => setImporting(true)}
            title="Bulk import"
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-accent hover:text-accent"
          >
            <Upload size={14} />
          </button>
          <button
            onClick={load}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-accent hover:text-accent"
          >
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-5">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Name, email, organisation…"
            className="pl-8 pr-3 py-1.5 w-64 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-accent"
          />
        </div>
        {sources.length > 0 && (
          <select
            value={source}
            onChange={(e) => setSource(e.target.value)}
            className="px-2 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            <option value="">All sources</option>
            {sources.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        )}
        <span className="text-xs text-gray-400 dark:text-gray-500 ml-auto">
          {filtered.length} of {leads.length}
        </span>
      </div>

      {leads.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <Inbox size={44} className="text-gray-300 dark:text-gray-600 mb-4" />
          <p className="text-gray-600 dark:text-gray-300 font-medium">No leads yet</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-md">
            The website posts to <code className="text-xs">/api/leads</code>. You can
            also add one by hand or import a list.
          </p>
        </div>
      ) : view === 'board' ? (
        <DragDropContext onDragEnd={onDragEnd}>
          <div className="flex gap-3 overflow-x-auto pb-4">
            {stages.map((stage) => {
              const rows = byStage.get(stage.id) || [];
              const over = !!stage.wipLimit && rows.length > stage.wipLimit;
              return (
                <Droppable droppableId={stage.id} key={stage.id}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={`w-72 shrink-0 rounded-xl p-2 transition-colors ${
                        snapshot.isDraggingOver
                          ? 'bg-accent/5 ring-1 ring-accent/30'
                          : 'bg-gray-100/70 dark:bg-gray-800/50'}`}
                    >
                      <div className="flex items-center gap-2 px-1 py-1.5 mb-1">
                        <span
                          className="w-2 h-2 rounded-full shrink-0"
                          style={{ backgroundColor: stage.color || '#94a3b8' }}
                        />
                        <span className="text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                          {stage.label}
                        </span>
                        <span className={`text-xs ${over ? 'text-amber-600 font-medium' : 'text-gray-400'}`}>
                          {rows.length}{stage.wipLimit ? ` / ${stage.wipLimit}` : ''}
                        </span>
                        {over && (
                          <span title={`Over the WIP limit of ${stage.wipLimit}`}>
                            <AlertTriangle size={12} className="text-amber-500" />
                          </span>
                        )}
                      </div>

                      <div className="space-y-1.5 min-h-10">
                        {rows.map((lead, index) => (
                          <Draggable draggableId={lead.id} index={index} key={lead.id}>
                            {(dragProvided, dragSnapshot) => (
                              <div
                                ref={dragProvided.innerRef}
                                {...dragProvided.draggableProps}
                                {...dragProvided.dragHandleProps}
                                onClick={() => setDetailItemId(lead.id)}
                                className={`rounded-lg border bg-white dark:bg-gray-800 px-2.5 py-2 cursor-pointer ${
                                  dragSnapshot.isDragging
                                    ? 'border-accent shadow-lg'
                                    : 'border-gray-200 dark:border-gray-700 hover:border-accent/50'
                                } ${moving === lead.id ? 'opacity-60' : ''}`}
                              >
                                <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                  {lead.name || lead.title || '(no name)'}
                                </div>
                                {lead.organisation && (
                                  <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                                    <Building2 size={10} /> {lead.organisation}
                                  </div>
                                )}
                                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                                  {lead.source && (
                                    <span className="px-1.5 py-0.5 rounded text-[10px] bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                                      {lead.source}
                                    </span>
                                  )}
                                  {(lead.submissionCount || 1) > 1 && (
                                    <span
                                      title={`${lead.submissionCount} submissions from this address`}
                                      className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300"
                                    >
                                      <MessageSquare size={9} /> {lead.submissionCount}
                                    </span>
                                  )}
                                  <span className="text-[10px] text-gray-400 ml-auto">
                                    {lead.createdAt
                                      ? new Date(lead.createdAt).toLocaleDateString()
                                      : ''}
                                  </span>
                                </div>
                                {canConvert(lead) && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setConverting(lead); }}
                                    className="mt-1.5 w-full flex items-center justify-center gap-1 px-1.5 py-0.5 rounded text-[10px] border border-gray-200 dark:border-gray-600 text-gray-500 hover:border-accent hover:text-accent"
                                  >
                                    <ArrowRightCircle size={10} /> Convert
                                  </button>
                                )}
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    </div>
                  )}
                </Droppable>
              );
            })}
          </div>
        </DragDropContext>
      ) : (
        <LeadList
          leads={filtered}
          stageFor={stageFor}
          onOpen={setDetailItemId}
          onConvert={setConverting}
        />
      )}

      {converting && (
        <ConvertLeadModal
          lead={converting}
          onClose={() => setConverting(null)}
          onConverted={() => { setConverting(null); load(); navigate('/crm/opportunities'); }}
        />
      )}
      {creating && (
        <NewLeadModal
          projectId={projectIds[0] || ''}
          onClose={() => setCreating(false)}
          onCreated={() => { setCreating(false); load(); }}
        />
      )}
      {importing && (
        <ImportLeadsModal
          projectId={projectIds[0] || ''}
          onClose={() => setImporting(false)}
          onImported={() => { setImporting(false); load(); }}
        />
      )}
    </div>
  );
}

/** Sorted by arrival, newest first — a lead's value decays with every hour. */
function LeadList({ leads, stageFor, onOpen, onConvert }: {
  leads: Lead[];
  stageFor: (id: string) => PipelineState | undefined;
  onOpen: (id: string) => void;
  onConvert: (lead: Lead) => void;
}) {
  const rows = useMemo(() =>
    [...leads].sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || '')),
    [leads]);

  if (rows.length === 0) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400 py-10 text-center">
        No leads match that filter.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 dark:bg-gray-800/60 text-xs text-gray-500 dark:text-gray-400">
          <tr>
            <th className="text-left font-medium px-3 py-2">Name</th>
            <th className="text-left font-medium px-3 py-2">Organisation</th>
            <th className="text-left font-medium px-3 py-2">Email</th>
            <th className="text-left font-medium px-3 py-2">Stage</th>
            <th className="text-left font-medium px-3 py-2">Source</th>
            <th className="text-left font-medium px-3 py-2">Arrived</th>
            <th className="px-3 py-2" />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
          {rows.map((lead) => {
            const stage = stageFor(lead.status);
            return (
              <tr key={lead.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/40">
                <td className="px-3 py-2">
                  <button onClick={() => onOpen(lead.id)}
                    className="text-gray-900 dark:text-gray-100 hover:text-accent text-left">
                    {lead.name || lead.title || '(no name)'}
                  </button>
                  {(lead.submissionCount || 1) > 1 && (
                    <span className="ml-2 text-[10px] text-amber-600">
                      ×{lead.submissionCount}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{lead.organisation}</td>
                <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{lead.email}</td>
                <td className="px-3 py-2">
                  <span className="inline-flex items-center gap-1.5 text-gray-600 dark:text-gray-300">
                    <span className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: stage?.color || '#94a3b8' }} />
                    {stage?.label || lead.status}
                  </span>
                </td>
                <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{lead.source}</td>
                <td className="px-3 py-2 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                  {lead.createdAt ? new Date(lead.createdAt).toLocaleDateString() : ''}
                </td>
                <td className="px-3 py-2 text-right">
                  {canConvert(lead) && (
                    <button onClick={() => onConvert(lead)}
                      className="text-xs text-gray-400 hover:text-accent">
                      Convert
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const FIELD = 'w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-accent';
const LABEL = 'block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1';

function Modal({ title, onClose, children, footer }: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  footer: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-xl bg-white dark:bg-gray-800 shadow-xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={16} />
          </button>
        </div>
        <div className="p-5 space-y-4 overflow-y-auto">{children}</div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100 dark:border-gray-700">
          {footer}
        </div>
      </div>
    </div>
  );
}

/**
 * Manual entry.
 *
 * The duplicate check runs as the email is typed, so a salesperson finds out
 * before filling in the rest of the record rather than after.
 */
function NewLeadModal({ projectId, onClose, onCreated }: {
  projectId: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [form, setForm] = useState({
    name: '', email: '', organisation: '', phone: '', message: '', source: 'manual',
  });
  const [duplicate, setDuplicate] = useState<Lead | null>(null);
  const [saving, setSaving] = useState(false);
  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!form.email.includes('@')) { setDuplicate(null); return; }
    let cancelled = false;
    const timer = setTimeout(() => {
      crmApi.checkDuplicateLead(form.email, projectId)
        .then((r) => { if (!cancelled) setDuplicate(r.duplicate && r.lead ? r.lead : null); })
        .catch(() => { if (!cancelled) setDuplicate(null); });
    }, 350);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [form.email, projectId]);

  const save = async (force = false) => {
    setSaving(true);
    try {
      await crmApi.createLead({ ...form, projectId, force });
      toast.success('Lead created');
      onCreated();
    } catch (err: any) {
      toast.error(err.message || 'Could not create the lead');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="New lead" onClose={onClose} footer={
      <>
        <button onClick={onClose}
          className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300">
          Cancel
        </button>
        <button onClick={() => save(!!duplicate)} disabled={saving || !form.name || !form.email}
          className="px-3 py-1.5 text-sm rounded-lg bg-accent text-white disabled:opacity-50">
          {saving ? 'Saving…' : duplicate ? 'Create anyway' : 'Create lead'}
        </button>
      </>
    }>
      {duplicate && (
        <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
          <strong>{duplicate.name}</strong> ({duplicate.displayId}) is already an open
          lead for this address, at stage <strong>{duplicate.status}</strong>. Creating
          another means two people may call the same prospect.
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={LABEL}>Name</label>
          <input className={FIELD} value={form.name}
            onChange={(e) => set('name', e.target.value)} />
        </div>
        <div>
          <label className={LABEL}>Email</label>
          <input className={FIELD} value={form.email}
            onChange={(e) => set('email', e.target.value)} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={LABEL}>Organisation</label>
          <input className={FIELD} value={form.organisation}
            onChange={(e) => set('organisation', e.target.value)} />
        </div>
        <div>
          <label className={LABEL}>Source</label>
          <select className={FIELD} value={form.source}
            onChange={(e) => set('source', e.target.value)}>
            {['manual', 'referral', 'event', 'website', 'import'].map((s) =>
              <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className={LABEL}>Notes</label>
        <textarea className={FIELD} rows={3} value={form.message}
          onChange={(e) => set('message', e.target.value)} />
      </div>
    </Modal>
  );
}

/** Bulk import. Preview first, always — it is not optional. */
function ImportLeadsModal({ projectId, onClose, onImported }: {
  projectId: string;
  onClose: () => void;
  onImported: () => void;
}) {
  const [text, setText] = useState('');
  const [preview, setPreview] = useState<any>(null);
  const [busy, setBusy] = useState(false);

  const parse = (): any[] => {
    const trimmed = text.trim();
    if (!trimmed) return [];
    if (trimmed.startsWith('[')) return JSON.parse(trimmed);
    // CSV: name,email,organisation — a header row is detected and skipped.
    const lines = trimmed.split('\n').map((l) => l.trim()).filter(Boolean);
    const start = /name/i.test(lines[0]) && /email/i.test(lines[0]) ? 1 : 0;
    return lines.slice(start).map((line) => {
      const [name, email, organisation, phone] = line.split(',').map((s) => s.trim());
      return { name, email, organisation, phone };
    });
  };

  const run = async (apply: boolean) => {
    setBusy(true);
    try {
      const rows = parse();
      if (!rows.length) { toast.error('Nothing to import'); return; }
      const result = await crmApi.importLeads({ projectId, rows, apply });
      setPreview(result);
      if (apply) {
        toast.success(`Imported ${result.created}, skipped ${result.duplicates} duplicate(s)`);
        onImported();
      }
    } catch (err: any) {
      toast.error(err.message || 'Could not parse or import that');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Import leads" onClose={onClose} footer={
      <>
        <button onClick={onClose}
          className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300">
          Cancel
        </button>
        <button onClick={() => run(false)} disabled={busy}
          className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 disabled:opacity-50">
          Preview
        </button>
        <button onClick={() => run(true)} disabled={busy || !preview}
          title={!preview ? 'Preview first' : ''}
          className="px-3 py-1.5 text-sm rounded-lg bg-accent text-white disabled:opacity-50">
          Import
        </button>
      </>
    }>
      <p className="text-xs text-gray-500 dark:text-gray-400">
        One per line as <code>name,email,organisation</code>, or paste a JSON array.
        A header row is skipped. Duplicates — in the file or already in the
        pipeline — are reported and not created twice.
      </p>
      <textarea
        className={`${FIELD} font-mono text-xs`}
        rows={8}
        spellCheck={false}
        placeholder={'name,email,organisation\nJane Roe,jane@acme.com,Acme Ltd'}
        value={text}
        onChange={(e) => { setText(e.target.value); setPreview(null); }}
      />
      {preview && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 text-xs space-y-1">
          <div className="text-gray-900 dark:text-gray-100 font-medium">
            {preview.rows} row(s): {preview.created} new, {preview.duplicates} duplicate,
            {' '}{preview.errors} error(s)
          </div>
          {(preview.detail?.duplicates || []).slice(0, 5).map((d: any, i: number) => (
            <div key={i} className="text-amber-700 dark:text-amber-400">
              row {d.row + 1}: {d.email} — {d.reason}
            </div>
          ))}
          {(preview.detail?.errors || []).slice(0, 5).map((e: any, i: number) => (
            <div key={i} className="text-rose-600 dark:text-rose-400">
              row {e.row + 1}: {e.error}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

/**
 * Propose / review / commit.
 *
 * Conversion is explicit rather than automatic because qualifying a lead is a
 * judgement, and an opportunity carries commercial fields nobody can infer from
 * a contact form. The server proposes what it can derive and leaves value and
 * close date blank; this is where a human fills them in.
 */
function ConvertLeadModal({ lead, onClose, onConverted }: {
  lead: Lead;
  onClose: () => void;
  onConverted: (opportunityId: string) => void;
}) {
  const sessionSeller = useCrmSellerStore((s) => s.sellerOrgId);
  const sellers = useCrmSellerStore((s) => s.sellers);
  const loadSellers = useCrmSellerStore((s) => s.loadSellers);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<Record<string, any>>({});
  const [already, setAlready] = useState(false);
  // Who buys and who sells. Offered here because it is the one moment someone
  // has the full context of the deal in front of them; both are also editable
  // on the opportunity afterwards, since the partner must be set before the
  // deal is won and that can be much later.
  const [orgs, setOrgs] = useState<CrmClient[]>([]);
  // Held as a decimal string so the field behaves normally while typing;
  // converted to integer minor units exactly once, on submit.
  const [amount, setAmount] = useState('');

  useEffect(() => {
    loadSellers();
    (async () => {
      try {
        const preview = await crmApi.previewConversion(lead.id);
        setAlready(preview.alreadyConverted);
        // The session's company sells unless the reviewer picks another.
        setForm({ ...(preview.proposal || {}), oppSellerOrgId: sessionSeller });
        // Non-fatal on failure: conversion still works without the pickers,
        // and both fields remain editable on the opportunity.
        try {
          const all = await crmApi.listClients();
          setOrgs(all.clients || []);
        } catch { /* pickers stay empty */ }
      } catch (err: any) {
        toast.error(err.message || 'Could not prepare the conversion');
        onClose();
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead.id]);

  const set = (k: string, v: any) => setForm((f) => ({ ...f, [k]: v }));
  const byOrgId = useMemo(
    () => Object.fromEntries(orgs.filter((o) => o.orgId).map((o) => [o.orgId!.toUpperCase(), o])),
    [orgs]);
  const nameOf = (orgId?: string) => (orgId ? byOrgId[orgId.toUpperCase()]?.name || orgId : '');
  // A reseller or a parent may sell a deal through; a client never services
  // anyone, because a partner-over-client grant follows this edge.
  const partners = orgs.filter((o) => o.orgType === 'reseller' || o.orgType === 'parent');
  const client = orgs.find((o) => o._id === form.oppClientId);
  const partner = form.oppPartnerOrgId ? byOrgId[String(form.oppPartnerOrgId).toUpperCase()] : undefined;

  // OFFERED, never set: a name match is not an identity match, and the lead
  // often names the company that WROTE in — 5MOS wrote in about Recology.
  const typed = (lead.organisation || '').trim().toLowerCase();
  const suggestion = typed && !form.oppClientId
    ? orgs.find((o) => (o.name || '').trim().toLowerCase() === typed)
    : undefined;

  /** Choosing a client proposes its standing partner — only into an empty
   *  partner field, so a deliberate choice is never overwritten. */
  const chooseClient = (clientId: string) => {
    const chosen = orgs.find((o) => o._id === clientId);
    setForm((f) => ({
      ...f,
      oppClientId: clientId,
      ...(chosen?.partnerOrgId && !f.oppPartnerOrgId
        ? { oppPartnerOrgId: chosen.partnerOrgId } : {}),
    }));
  };

  const submit = async () => {
    const parsed = Number(amount.replace(/[^0-9.]/g, ''));
    if (amount && !Number.isFinite(parsed)) {
      toast.error('Value must be a number');
      return;
    }
    setSaving(true);
    try {
      const res = await crmApi.convertLead(lead.id, {
        ...form,
        // Round at the boundary, once. Math.round rather than truncation so
        // 1.005 does not silently become 1.00.
        oppValueMinor: amount ? Math.round(parsed * 100) : 0,
      });
      toast.success(`Converted to ${res.opportunity.displayId}`);
      onConverted(res.opportunity.id);
    } catch (err: any) {
      toast.error(err.message || 'Conversion failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Convert lead to opportunity" onClose={onClose} footer={
      <>
        <button onClick={onClose}
          className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300">
          Cancel
        </button>
        {!already && (
          <button onClick={submit} disabled={saving || loading}
            className="px-3 py-1.5 text-sm rounded-lg bg-accent text-white disabled:opacity-50">
            {saving ? 'Converting…' : 'Create opportunity'}
          </button>
        )}
      </>
    }>
      {loading ? (
        <div className="py-6 flex justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-2 border-accent border-t-transparent" />
        </div>
      ) : already ? (
        <p className="text-sm text-gray-600 dark:text-gray-300">
          This lead has already been converted. Converting again would create a
          second opportunity for one deal and double the forecast.
        </p>
      ) : (
        <>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Contact details carry across. Value and close date are blank because
            nothing in a contact form implies them — fill in what you know.
          </p>
          <div>
            <label className={LABEL}>Opportunity name</label>
            <input className={FIELD} value={form.title || ''}
              onChange={(e) => set('title', e.target.value)} />
          </div>

          {/* Which of our companies is selling. From the session's "Selling
              as", changeable for this deal. */}
          <div>
            <label className={LABEL}>Selling as</label>
            <select className={FIELD} value={form.oppSellerOrgId || ''}
              onChange={(e) => set('oppSellerOrgId', e.target.value)}>
              <option value="">— not set —</option>
              {sellers.map((s) => (
                <option key={s._id} value={s.orgId}>{s.name}</option>
              ))}
            </select>
          </div>

          {/* Who buys, and who it is sold through. Neither is guessed from the
              lead: a name match is not an identity match, and the partner
              becomes a write-once binding on the research platform. */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={LABEL}>Client (end customer)</label>
              <select className={FIELD} value={form.oppClientId || ''}
                onChange={(e) => chooseClient(e.target.value)}>
                <option value="">— not set —</option>
                {orgs.map((c) => (
                  <option key={c._id} value={c._id}>
                    {c.name}{c.orgId ? ` · ${c.orgId}` : ''}
                  </option>
                ))}
              </select>
              {suggestion && (
                <button type="button" onClick={() => chooseClient(suggestion._id)}
                  className="mt-1 text-[11px] text-accent hover:underline text-left">
                  The lead names “{lead.organisation}” — use {suggestion.name}?
                </button>
              )}
              {client?.parentOrgId && (
                <p className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">
                  Parent organisation: {nameOf(client.parentOrgId)}
                </p>
              )}
            </div>
            <div>
              <label className={LABEL}>Partner (reseller)</label>
              <select className={FIELD} value={form.oppPartnerOrgId || ''}
                onChange={(e) => set('oppPartnerOrgId', e.target.value)}>
                <option value="">— not set —</option>
                {partners.map((r) => (
                  <option key={r._id} value={r.orgId || ''}>
                    {r.name}{r.orgId ? ` · ${r.orgId}` : ''}
                  </option>
                ))}
              </select>
              {partner?.parentOrgId && (
                <p className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">
                  Parent organisation: {nameOf(partner.parentOrgId)}
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={LABEL}>Account</label>
              <input className={FIELD} value={form.oppAccount || ''}
                onChange={(e) => set('oppAccount', e.target.value)} />
            </div>
            <div>
              <label className={LABEL}>Source</label>
              <select className={FIELD} value={form.oppSource || 'inbound'}
                onChange={(e) => set('oppSource', e.target.value)}>
                {['inbound', 'outbound', 'referral', 'expansion', 'rfp']
                  .map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className={LABEL}>Estimated value</label>
              <input className={FIELD} inputMode="decimal" placeholder="0.00"
                value={amount} onChange={(e) => setAmount(e.target.value)} />
            </div>
            <div>
              <label className={LABEL}>Currency</label>
              <input className={FIELD} value={form.oppCurrency || 'USD'}
                onChange={(e) => set('oppCurrency', e.target.value.toUpperCase())} />
            </div>
          </div>
          <div>
            <label className={LABEL}>Expected close date</label>
            <input type="date" className={FIELD}
              value={form.oppExpectedCloseDate || ''}
              onChange={(e) => set('oppExpectedCloseDate', e.target.value)} />
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Client and partner can be set later on the opportunity — but the
            partner must be there before the deal is won, or it will not
            originate a research engagement.
          </p>
        </>
      )}
    </Modal>
  );
}
